import React, { createContext, useState, useEffect, useContext } from 'react';
import { apiService } from '../services/api';

const AppContext = createContext();

export const defaultLinks = [
  {
    id: '1',
    title: 'Senior Travel Guides',
    description: 'Practical, easy-to-follow resources designed specifically for seniors who want more clarity and comfort on their next journey.',
    url: 'https://www.youtube.com/@GoldenCompassTravelGuides',
    imageUrl: 'https://images.unsplash.com/photo-1590523277543-a94d2e4eb00b?auto=format&fit=crop&q=80&w=800',
    isComingSoon: false
  },
  {
    id: '2',
    title: 'Solo Travel Guide',
    description: 'Build your confidence and explore the world independently with our niche solo travel resources.',
    url: 'https://www.youtube.com/@GoldenCompassTravelGuides',
    imageUrl: 'https://images.unsplash.com/photo-1503220317375-aaad61436b1b?auto=format&fit=crop&q=80&w=800',
    isComingSoon: false
  },
  {
    id: '5',
    title: 'Travel Shopping Guide',
    description: 'Discover essential travel items and curated shopping hacks designed to make your next trip smoother and more organized.',
    url: 'https://www.youtube.com/@GoldenCompassTravelGuides',
    imageUrl: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&q=80&w=800',
    isComingSoon: false
  },
  {
    id: '4',
    title: 'Free Calculator',
    description: 'A helpful tool designed to reduce confusion and help you budget for your next chapter in travel.',
    url: 'https://www.youtube.com/@GoldenCompassTravelGuides',
    imageUrl: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=800',
    isComingSoon: false
  },
  {
    id: '3',
    title: 'Travel Business',
    description: 'A beginner-friendly starting point for those curious about becoming a travel advisor or starting a travel business.',
    url: 'https://www.youtube.com/@GoldenCompassTravelGuides',
    imageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=800',
    isComingSoon: false
  },
  {
    id: '6',
    title: 'Travel Agency U',
    description: 'The ultimate next step for aspiring travel entrepreneurship. Deep-dive training for modern advisors.',
    url: 'https://www.youtube.com/@GoldenCompassTravelGuides',
    imageUrl: 'https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&q=80&w=800',
    isComingSoon: true
  }
];

export const defaultSettings = {
  youtubeUrl: 'https://www.youtube.com/@GoldenCompassTravelGuides',
  seo: {
    metaTitle: 'Golden Compass Travel Guides | Start Here',
    metaDescription: 'Travel guidance for real people, real questions, and real next steps.',
    keywords: 'travel guides, senior travel, solo travel'
  },
  home: {
    heroTitle: 'Start Here',
    heroSubtitle: 'Welcome to Golden Compass Travel Guides.',
    heroDescription: 'Whether you’re looking for travel guides or business resources, you’re in the right place.',
    introQuote: 'Golden Compass Travel Guides was created to make travel easier to explore and easier to understand. Some people come here looking for destination ideas or practical travel tips. Others are curious about solo travel, senior-friendly travel, or whether the travel industry could become part of their next chapter.',
    findHereTitle: "What You’ll Find at Golden Compass Travel Guides",
    findHereDescription: "At Golden Compass Travel Guides, the goal is simple: make travel more approachable and more useful.",
    whoIsForTitle: "Who This Is For"
  },
  about: {
    heroTitle: 'About Golden Compass',
    heroSubtitle: 'Travel guidance for real people.',
    heroDescription: 'Created to help people explore travel with more clarity and confidence.'
  }
};

export const AppProvider = ({ children }) => {
  const [links, setLinks] = useState(defaultLinks);
  const [settings, setSettings] = useState(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const remoteLinks = await apiService.getLinks();
      const remoteSettings = await apiService.getSettings();
      
      if (remoteLinks && Array.isArray(remoteLinks) && remoteLinks.length > 0) {
        setLinks(remoteLinks);
        setIsLive(true);
      } else {
        setLinks(defaultLinks);
        setIsLive(false);
      }

      if (remoteSettings) {
        // Deep merge remote settings with defaults to ensure all keys exist
        setSettings({
          ...defaultSettings,
          ...remoteSettings,
          home: { ...defaultSettings.home, ...remoteSettings.home },
          seo: { ...defaultSettings.seo, ...remoteSettings.seo }
        });
        
        // Update document title for SEO
        if (remoteSettings.seo?.metaTitle) {
          document.title = remoteSettings.seo.metaTitle;
        }
      }
    } catch (err) {
      console.error('AppData Sync Issue:', err.message);
      setErrorMessage(err.message);
      setLinks(defaultLinks);
      setIsLive(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateLink = async (id, updatedData) => {
    try {
      if (isLive) {
        await apiService.updateLink(id, updatedData);
      }
      setLinks(prev => prev.map(l => l.id === id ? { ...l, ...updatedData } : l));
      return true;
    } catch (err) {
      throw err;
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await apiService.updateSettings('site_settings', newSettings);
      setSettings(prev => ({ ...prev, ...newSettings }));
      if (newSettings.seo?.metaTitle) {
        document.title = newSettings.seo.metaTitle;
      }
      return true;
    } catch (err) {
      throw err;
    }
  };

  return (
    <AppContext.Provider value={{
      links, 
      settings, 
      updateLink, 
      updateSettings, 
      isLoading, 
      isLive, 
      errorMessage, 
      refresh: fetchData,
      setLinks,
      setIsLive
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);